/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Button, Form, Modal } from '@mairistem/composants';

/** Champs modifiables d'un document déjà déposé. */
export interface ModificationDocument {
  nom: string;
  description: string;
}

interface ModalModifierDocumentProps {
  open: boolean;
  /** Nom actuel, proposé à la modification. */
  nom?: string;
  /** Description actuelle, portée par `dc_zsaisie`. */
  description?: string;
  onClose: () => void;
  onConfirm: (modification: ModificationDocument) => void;
}

/**
 * Modification d'un document du dossier.
 *
 * Nom et description seulement : `PUT /documents/{id}` interdit
 * `identifiantActe`, le rattachement à l'acte est figé à la création, et le
 * contenu du fichier ne se remplace pas — il faut redéposer un document.
 */
const ModalModifierDocument = (props: ModalModifierDocumentProps) => {
  const { open, nom = '', description = '', onClose, onConfirm } = props;

  const [valeurNom, setValeurNom] = React.useState(nom);
  const [valeurDescription, setValeurDescription] = React.useState(description);

  React.useEffect(() => {
    if (!open) return;

    setValeurNom(nom);
    setValeurDescription(description);
  }, [open, nom, description]);

  const valide = valeurNom.trim().length > 0;

  const handleConfirm = React.useCallback(() => {
    if (!valide) return;

    onConfirm({
      nom: valeurNom.trim(),
      description: valeurDescription.trim(),
    });
  }, [valide, valeurNom, valeurDescription, onConfirm]);

  return (
    <Modal open={open} onClose={onClose} size="tiny">
      <Modal.Header>Modifier le document</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            autoFocus
            label="Nom du document"
            data-testid="champ-nom-document"
            value={valeurNom}
            onChange={(_e: unknown, data: { value: string }) =>
              setValeurNom(data.value)
            }
          />
          {/*
            La description est facultative : la vider est une modification
            légitime, la liste n'affiche alors plus rien sous le nom.
          */}
          <Form.Input
            label="Description"
            data-testid="champ-description-document"
            value={valeurDescription}
            onChange={(_e: unknown, data: { value: string }) =>
              setValeurDescription(data.value)
            }
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic content="Annuler" onClick={onClose} />
        <Button
          primary
          content="Enregistrer"
          disabled={!valide}
          onClick={handleConfirm}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalModifierDocument;
